/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {AuthContextInterface} from '../types/types';
import {app, getCurrentUser, signOut} from '../providers/Account';
const querystring = require('querystring');

export const AuthContext = createContext<AuthContextInterface>(null!);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({children}: {children: React.ReactNode}) => {
  const [currentUser, setCurrentUser] = useState({});

  // init() runs at module load and copies whatever is in localStorage into
  // app.session, so clearing storage on its own leaves a live token in memory
  // that getCurrentUser() will happily accept. Both have to go.
  const clearSession = () => {
    localStorage.clear();

    app.session = {
      lastAuthUser: null,
      idToken: null,
      accessToken: null,
      refreshToken: null,
    };
    app.user = undefined;
  };

  const logOut = useCallback(() => {
    // Signing out has to finish locally even when the global signout call
    // fails. It returns 401 whenever the access token is already expired or
    // revoked, and gating the redirect on success left the user sitting on an
    // authenticated page with no way out.
    const finishSignOut = () => {
      clearSession();
      window.location.href = '/';
    };

    // Attempt the remote revoke first, while the token is still in memory.
    signOut(app)
      .then(finishSignOut)
      .catch((error: any) => {
        console.warn('Global signout failed, clearing session anyway:', error);
        finishSignOut();
      });
  }, []);

  const login = (user: any) => {
    setCurrentUser({user});
  };

  const checkLogOut = () => {
    const queryString = window.location.search.substring(1);
    const queryParams = querystring.parse(queryString);
    if (queryParams.action === 'logout') {
      logOut();
    }
  };

  useEffect(() => {
    // business/admin/internal append clear=true when they sign a user out.
    // Accounts is a separate origin with its own localStorage, and SignInView
    // only cleared storage -- app.session still held a live token from init(),
    // so getCurrentUser() below succeeded and PublicRoute redirected straight
    // to /tenants. The user could never actually get signed out.
    const queryParams = querystring.parse(window.location.search.substring(1));

    if (queryParams.clear) {
      clearSession();
      return;
    }

    checkLogOut();
    getCurrentUser(app)
      .then(({user}: any) => {
        login(user);
      })
      .catch(() => {
        if (window.location.pathname !== '/') logOut();
      });
  }, []);

  return (
    <AuthContext.Provider value={{currentUser, login, logOut}}>
      {children}
    </AuthContext.Provider>
  );
};
